(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const seen = new WeakSet();
  const animations = new Set();
  const announcements = new Map();
  const languageMenus = new Map();

  function animate(target, frames, options) {
    if (reducedMotion.matches || !target?.animate) return;
    const animation = target.animate(frames, options);
    animations.add(animation);
    const release = () => animations.delete(animation);
    animation.finished.then(release, release);
  }

  function animatePhoto(image, frames, options) {
    if (!image || reducedMotion.matches) return;
    const play = () => {
      if (!image.isConnected || !image.naturalWidth) return;
      const bounds = image.getBoundingClientRect();
      if (bounds.bottom <= 0 || bounds.top >= window.innerHeight || bounds.right <= 0 || bounds.left >= window.innerWidth) return;
      animate(image, frames, options);
    };
    if (image.complete) play();
    else image.addEventListener('load', play, { once: true });
  }

  const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach(({ target, isIntersecting }) => {
      if (!isIntersecting) return;
      observer.unobserve(target);
      if (target.matches('.dw-moments')) {
        const bounds = target.getBoundingClientRect();
        const photos = [...target.querySelectorAll('.dw-moment img')].filter((image) => {
          const photo = image.getBoundingClientRect();
          return photo.right > bounds.left && photo.left < bounds.right;
        });
        photos.forEach((image, index) => {
          const direction = index % 2 === 0 ? -1 : 1;
          animatePhoto(image, [
            { transform: `translateY(12px) rotate(${direction * 2.4}deg) scale(.94)` },
            { transform: 'none' },
          ], { duration: 640, delay: Math.min(index, 2) * 70, easing: 'cubic-bezier(.16,1,.3,1)', fill: 'backwards' });
        });
        return;
      }
      target.querySelectorAll('.dw-story-image img, .dw-story-second img').forEach((image, index) => {
        animatePhoto(image, [{ transform: 'translateY(10px) scale(1.025)' }, { transform: 'none' }], { duration: 480, delay: index * 70, easing: 'cubic-bezier(.16,1,.3,1)' });
      });
      const caption = target.querySelector('.dw-image-caption');
      if (caption) animate(caption, [{ transform: 'translateX(-8px) rotate(-3deg)' }, { transform: 'none' }], { duration: 520, easing: 'cubic-bezier(.16,1,.3,1)' });
    });
  }, { threshold: 0.2 }) : null;

  function initializeAnnouncements(root) {
    root.querySelectorAll('[data-dw-announcement]').forEach((bar) => {
      if (announcements.has(bar)) return;
      const messages = [...bar.querySelectorAll('[data-dw-message]')];
      const button = bar.querySelector('.dw-announcement-toggle');
      if (messages.length < 2 || !button) return;
      const listeners = new AbortController();
      const options = { signal: listeners.signal };
      let active = 0;
      let paused = false;
      let hovered = false;
      let focused = false;
      let timer;
      const show = (index) => {
        active = index;
        messages.forEach((message, i) => {
          message.dataset.active = String(i === active);
          message.setAttribute('aria-hidden', String(i !== active));
        });
      };
      const sync = () => {
        clearTimeout(timer);
        button.hidden = reducedMotion.matches;
        button.setAttribute('aria-label', paused ? button.dataset.resumeLabel : button.dataset.pauseLabel);
        button.setAttribute('aria-pressed', String(paused));
        button.querySelector('[data-dw-pause]').hidden = paused;
        button.querySelector('[data-dw-play]').hidden = !paused;
        if (reducedMotion.matches) show(0);
        if (reducedMotion.matches || paused || hovered || focused || document.hidden) return;
        timer = setTimeout(() => {
          show((active + 1) % messages.length);
          animate(messages[active], [{ opacity: 0.4 }, { opacity: 1 }], { duration: 220, easing: 'ease-out' });
          sync();
        }, 10000);
      };
      button.addEventListener('click', () => { paused = !paused; sync(); }, options);
      bar.addEventListener('mouseenter', () => { hovered = true; sync(); }, options);
      bar.addEventListener('mouseleave', () => { hovered = false; sync(); }, options);
      bar.addEventListener('focusin', () => { focused = true; sync(); }, options);
      bar.addEventListener('focusout', (event) => {
        focused = bar.contains(event.relatedTarget);
        sync();
      }, options);
      document.addEventListener('visibilitychange', sync, options);
      announcements.set(bar, {
        sync,
        destroy() {
          clearTimeout(timer);
          listeners.abort();
          announcements.delete(bar);
        },
      });
      sync();
    });
  }

  function initialize(root = document) {
    initializeAnnouncements(root);
    root.querySelectorAll('[data-dw-language-menu]').forEach((menu) => {
      if (languageMenus.has(menu)) return;
      const listeners = new AbortController();
      const options = { signal: listeners.signal };
      menu.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !menu.open) return;
        event.preventDefault();
        menu.open = false;
        menu.querySelector('summary').focus();
      }, options);
      menu.addEventListener('focusout', (event) => {
        if (!menu.contains(event.relatedTarget)) menu.open = false;
      }, options);
      document.addEventListener('click', (event) => {
        if (!menu.contains(event.target)) menu.open = false;
      }, options);
      languageMenus.set(menu, () => {
        listeners.abort();
        languageMenus.delete(menu);
      });
    });
    root.querySelectorAll('.dw-story').forEach((surface) => {
      if (seen.has(surface)) return;
      seen.add(surface);
      observer?.observe(surface);
    });

    root.querySelectorAll('.dw-faq-list details').forEach((details) => {
      if (seen.has(details)) return;
      seen.add(details);
      details.addEventListener('toggle', () => {
        if (details.open) animate(details.querySelector('.rte'), [{ opacity: 0.65, transform: 'translateY(-4px)' }, { opacity: 1, transform: 'none' }], { duration: 180, easing: 'ease-out' });
      });
    });

    root.querySelectorAll('.dw-moments').forEach((gallery) => {
      if (seen.has(gallery)) return;
      seen.add(gallery);
      observer?.observe(gallery);
      const controls = gallery.previousElementSibling?.querySelector('.dw-moments-controls');
      if (!controls) return;
      controls.hidden = false;
      const buttons = [...controls.querySelectorAll('button')];
      const update = () => {
        buttons[0].disabled = gallery.scrollLeft < 4;
        buttons[1].disabled = gallery.scrollLeft + gallery.clientWidth >= gallery.scrollWidth - 4;
      };
      buttons.forEach((button) => button.addEventListener('click', () => {
        const photo = gallery.querySelector('.dw-moment');
        if (!photo) return;
        const step = photo.getBoundingClientRect().width + parseFloat(getComputedStyle(gallery).gap);
        gallery.scrollBy({ left: step * Number(button.dataset.dwGalleryDirection), behavior: reducedMotion.matches ? 'instant' : 'smooth' });
      }));
      gallery.addEventListener('scroll', update, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(gallery);
      update();
    });

    root.querySelectorAll('.dw-gallery').forEach((gallery) => {
      if (seen.has(gallery)) return;
      seen.add(gallery);
      const thumbnails = gallery.nextElementSibling;
      if (!thumbnails?.classList.contains('dw-gallery-thumbnails')) return;
      const images = [...gallery.querySelectorAll('img')];
      const links = [...thumbnails.querySelectorAll('a')];
      links.forEach((link, index) => link.addEventListener('click', (event) => {
        event.preventDefault();
        gallery.scrollTo({ left: images[index].offsetLeft - images[0].offsetLeft, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
      }));
      let frame = 0;
      const update = () => {
        frame = 0;
        if (!gallery.isConnected) return;
        const left = gallery.getBoundingClientRect().left;
        const distances = images.map((image) => Math.abs(image.getBoundingClientRect().left - left));
        const active = distances.indexOf(Math.min(...distances));
        links.forEach((link, index) => link.setAttribute('aria-current', String(index === active)));
      };
      gallery.addEventListener('scroll', () => {
        if (!frame) frame = requestAnimationFrame(update);
      }, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(gallery);
      update();
    });
  }

  function syncQuantityOptions() {
    document.querySelectorAll('[data-dw-quantity-options]').forEach((choices) => {
      const form = document.getElementById(choices.dataset.formId);
      const quantity = form?.elements.namedItem('quantity');
      if (!quantity) return;
      choices.querySelectorAll('input[type="radio"]').forEach((radio) => {
        radio.checked = radio.value === quantity.value;
        const max = Number(quantity.getAttribute('max'));
        radio.disabled = max > 0 && Number(radio.value) > max;
      });
      document.querySelectorAll('[data-dw-sticky-total]').forEach((total) => {
        const unit = BigInt(total.dataset.unitPrice);
        const amount = unit * BigInt(Math.max(1, parseInt(quantity.value, 10) || 1));
        const decimal = `${amount / 100n}.${String(amount % 100n).padStart(2, '0')}`;
        total.textContent = new Intl.NumberFormat(document.documentElement.lang, { style: 'currency', currency: total.dataset.currency }).format(decimal);
      });
    });
  }

  document.addEventListener('change', (event) => {
    const choices = event.target.closest('[data-dw-quantity-options]');
    if (choices && event.target.matches('input[type="radio"]')) {
      const quantity = document.getElementById(choices.dataset.formId)?.elements.namedItem('quantity');
      if (quantity) {
        quantity.value = event.target.value;
        quantity.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    if (choices || event.target.name === 'quantity') syncQuantityOptions();
  });

  function syncPurchaseButtons() {
    syncQuantityOptions();
    const source = document.querySelector('product-info .product-form__submit, .dw-hero .product-form__submit');
    document.querySelectorAll('[data-dw-sticky-submit]').forEach((button) => {
      if (!source || !source.form) {
        button.disabled = true;
        return;
      }
      const loading = source.getAttribute('aria-disabled') === 'true';
      button.setAttribute('form', source.form.getAttribute('id'));
      button.disabled = source.disabled || loading;
      button.setAttribute('aria-busy', String(loading));
      const buyButton = source.form.querySelector('[name="return_to"]');
      button.textContent = loading ? button.dataset.loadingLabel : buyButton.querySelector('span').textContent.trim();
    });
  }

  initialize();
  syncPurchaseButtons();
  const product = document.querySelector('product-info, .dw-hero');
  if (product && 'IntersectionObserver' in window) {
    const purchaseAction = product.querySelector('.dw-buy-now');
    if (purchaseAction) new IntersectionObserver(([entry]) => {
      document.querySelectorAll('.dw-sticky').forEach((bar) => {
        bar.hidden = entry.intersectionRatio >= 0.95;
      });
    }, { threshold: [0, 0.95] }).observe(purchaseAction);
  }
  if (product) new MutationObserver(syncPurchaseButtons).observe(product, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
    syncPurchaseButtons();
  });
  document.addEventListener('shopify:section:unload', (event) => {
    languageMenus.forEach((destroy, menu) => {
      if (event.target.contains(menu)) destroy();
    });
    announcements.forEach((announcement, bar) => {
      if (event.target.contains(bar)) announcement.destroy();
    });
    event.target.querySelectorAll('.dw-story, .dw-moments').forEach((surface) => observer?.unobserve(surface));
    animations.forEach((animation) => {
      if (event.target.contains(animation.effect?.target)) animation.cancel();
    });
  });
  reducedMotion.addEventListener('change', () => {
    announcements.forEach((announcement) => announcement.sync());
    if (reducedMotion.matches) animations.forEach((animation) => animation.finish());
  });

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-dw-apply-discount]');
    if (!button || button.disabled) return;
    const widget = button.closest('[data-dw-discount]');
    const input = widget.querySelector('input');
    const code = input.value.trim().toUpperCase();
    if (!code) { input.focus(); return; }
    const location = widget.dataset.location;
    const carts = [...document.querySelectorAll('cart-items, cart-drawer-items')];
    const sections = carts.flatMap((cart) => cart.getSectionsToRender());
    button.disabled = true;
    widget.querySelector('[data-dw-discount-status]').textContent = '';
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: code, sections: [...new Set(sections.map((s) => s.section))], sections_url: window.location.pathname })
      });
      const cart = await response.json();
      if (!response.ok || cart.errors) throw new Error(cart.description || cart.errors || response.status);
      sections.forEach((section) => {
        const parent = document.getElementById(section.id);
        const html = cart.sections?.[section.section];
        if (!parent || !html) return;
        const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
        if (source) (parent.querySelector(section.selector) || parent).innerHTML = source.innerHTML;
      });
      const updated = document.querySelector('[data-dw-discount][data-location="' + location + '"]');
      const discounts = [...(cart.cart_level_discount_applications || []), ...cart.items.flatMap((item) => item.discounts || [])];
      const applied = discounts.some((discount) => discount.title.toUpperCase() === code);
      if (updated) {
        updated.querySelector('input').value = code;
        updated.querySelector('[data-dw-discount-status]').textContent = applied ? widget.dataset.applied : widget.dataset.invalid;
        const nextButton = updated.querySelector('[data-dw-apply-discount]');
        const drawer = updated.closest('cart-drawer.active');
        if (drawer) trapFocus(drawer.querySelector('.drawer__inner'), nextButton);
        else nextButton.focus();
      }
    } catch (error) {
      widget.querySelector('[data-dw-discount-status]').textContent = widget.dataset.error;
    } finally {
      button.disabled = false;
    }
  });
})();
